import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, ComputerStatus, Group, ThreadSnapshot } from "@rakazo/contracts";
import { isIdentityConfirmationRequired } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { Monitor, Settings, X } from "lucide-react";
import type { ComponentProps, Dispatch, ReactNode, SetStateAction } from "react";
import { ComputerMaintenanceActions } from "../../components/ComputerMaintenanceActions";
import { useIdentityConfirmation } from "../../components/ConfirmIdentityDialog";
import type { Panel } from "../../lib/right-panel-state";
import type { createSpaceRpc } from "../../lib/rpc";
import { computerPanelNeedsMaintenance } from "../../lib/thread-events";
import { CreateGroupForm, GroupSettings } from "../GroupPanel";
import { ResizableSidePanel } from "../ResizableSidePanel";
import { RoutineEditor } from "../RoutineEditor";
import type { SettingsSection } from "../shell-overlay";
import { AgentImportForm } from "./agent-import";
import { BotPanelTabs, isBotPanelTab } from "./bot-details-panel";
import { BotSettingsDialog } from "./bot-settings-dialog";
import { firstThreadRoute } from "./use-shell-navigation";

/** The panel beside the chat: a bot's tabs, a routine, a group, or an import. */
export function ShellSidePanel({
  rpc,
  panel,
  setPanel,
  active,
  activeGroup,
  bots,
  groups,
  setBots,
  setGroups,
  navigate,
  refreshBots,
  refreshThread,
  refreshGroupThread,
  computer,
  computerOpen,
  booting,
  computerPanel,
  detailsPanel,
  libraryPanel,
  filePanel,
  routineEditorProps,
  openBot,
  openCreatedBot,
  modelFocusRequest,
  openSettings,
  onClearBot,
}: {
  rpc: ReturnType<typeof createSpaceRpc>;
  panel: Panel;
  setPanel: (panel: Panel) => void;
  active: Bot | undefined;
  activeGroup: Group | undefined;
  bots: Bot[];
  groups: Group[];
  setBots: Dispatch<SetStateAction<Bot[]>>;
  setGroups: Dispatch<SetStateAction<Group[]>>;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  refreshBots: (includeArchived?: boolean) => Promise<void>;
  refreshThread: (botId: string) => Promise<ThreadSnapshot>;
  refreshGroupThread: (groupId: string) => Promise<ThreadSnapshot>;
  computer: ComputerStatus | null;
  computerOpen: boolean;
  booting: boolean;
  computerPanel: ReactNode;
  detailsPanel: ReactNode;
  libraryPanel: ReactNode;
  /** The file opened beside the chat, with its own header. */
  filePanel: ReactNode;
  routineEditorProps: ComponentProps<typeof RoutineEditor> | null;
  openBot: (botId: string) => void;
  openCreatedBot: (bot: Bot) => void;
  modelFocusRequest: number;
  openSettings: (section: SettingsSection) => void;
  onClearBot: (bot: Bot) => void;
}) {
  const { t } = useLingui();
  const { withIdentityConfirmation, identityDialog } = useIdentityConfirmation();
  const botTab = isBotPanelTab(panel) ? panel : null;
  // A bot's settings open as a dialog over the chat, not in this panel.
  const open = Boolean(
    panel &&
      panel !== "settings" &&
      (active || (activeGroup && !botTab) || panel === "import-agent"),
  );
  const maintenance =
    active &&
    panel === "computer" &&
    !computerOpen &&
    computerPanelNeedsMaintenance(computer?.state, booting) ? (
      <ComputerMaintenanceActions
        botId={active.id}
        computer={computer}
        onChanged={async () => {
          await refreshThread(active.id);
        }}
      />
    ) : null;
  const closeButton = (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={t`Close panel`}
      onClick={() => setPanel(null)}
    >
      <X size={16} strokeWidth={1.8} />
    </Button>
  );

  function replaceBot(updated: Bot) {
    setBots((current) => current.map((bot) => (bot.id === updated.id ? updated : bot)));
    // Saved already; a failed roster refresh must not undo the edit.
    void refreshBots().catch(() => undefined);
  }

  async function createGroup(input: { name: string; botIds: string[] }) {
    const group = await rpc.groups.create(input);
    setGroups((current) =>
      current.some((item) => item.id === group.id) ? current : [group, ...current],
    );
    navigate(`/app/g/${group.id}`);
    setPanel(null);
    await refreshBots().catch(() => undefined);
  }

  return (
    <>
      <ResizableSidePanel open={open} panel={panel ?? "closed"}>
        {open ? (
          <div className="rk-scroll h-full w-full overflow-y-auto px-5 py-[17px]">
            {active && botTab ? (
              <div className="mb-4 flex items-center justify-between gap-2">
                <BotPanelTabs value={botTab} onChange={setPanel} />
                <div className="flex gap-1">
                  {maintenance}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t`Show settings`}
                    onClick={() => setPanel("settings")}
                    className="text-muted-foreground"
                  >
                    <Settings size={16} strokeWidth={1.7} />
                  </Button>
                  {closeButton}
                </div>
              </div>
            ) : panel !== "routine" &&
              panel !== "file" &&
              panel !== "create-group" &&
              panel !== "group-settings" &&
              panel !== "import-agent" ? (
              <div className="mb-4 flex items-center justify-between">
                {!active && panel !== "settings" ? (
                  <span className="text-[13.5px] text-muted-foreground">
                    <Trans>Group</Trans>
                  </span>
                ) : null}
                <div className="ml-auto flex gap-1">
                  {active ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t`Show computer`}
                      onClick={() => setPanel("computer")}
                      className="text-foreground"
                    >
                      <Monitor size={16} strokeWidth={1.7} />
                    </Button>
                  ) : null}
                  {closeButton}
                </div>
              </div>
            ) : null}
            {panel === "details" && active ? detailsPanel : null}
            {panel === "library" && active ? libraryPanel : null}
            {panel === "computer" && active ? computerPanel : null}
            {panel === "file" ? filePanel : null}
            {panel === "create-group" ? (
              <CreateGroupForm
                bots={bots}
                onCancel={() => setPanel(null)}
                onCreate={(input) => createGroup(input)}
                onOpenBot={(botId) => {
                  setPanel(null);
                  openBot(botId);
                }}
              />
            ) : null}
            {panel === "group-settings" && activeGroup ? (
              <GroupSettings
                key={activeGroup.id}
                group={activeGroup}
                bots={bots}
                onSave={async (input) => {
                  const updated = await rpc.groups.update({ groupId: activeGroup.id, ...input });
                  setGroups((current) =>
                    current.map((group) => (group.id === updated.id ? updated : group)),
                  );
                  void Promise.all([refreshBots(), refreshGroupThread(activeGroup.id)]).catch(
                    () => undefined,
                  );
                }}
                onRemove={async () => {
                  await rpc.groups.remove({ groupId: activeGroup.id });
                  const remainingGroups = groups.filter((group) => group.id !== activeGroup.id);
                  setGroups(remainingGroups);
                  setPanel(null);
                  navigate(firstThreadRoute(bots, remainingGroups), { replace: true });
                  await refreshBots().catch(() => undefined);
                }}
                onClose={() => setPanel(null)}
                onOpenBot={openBot}
                covered={computerOpen}
              />
            ) : null}
            {panel === "import-agent" ? (
              <AgentImportForm
                onClose={() => setPanel(null)}
                onCreated={(bot) => {
                  openCreatedBot(bot);
                  void refreshBots().catch(() => undefined);
                }}
              />
            ) : null}
            {panel === "routine" && routineEditorProps ? (
              <RoutineEditor {...routineEditorProps} />
            ) : null}
          </div>
        ) : null}
      </ResizableSidePanel>
      {panel === "settings" && active ? (
        <BotSettingsDialog
          key={active.id}
          bot={active}
          initialSection={modelFocusRequest ? "model" : "profile"}
          currentSuperBot={bots.find((bot) => bot.superBot && bot.id !== active.id)?.name}
          onClose={() => setPanel(null)}
          actions={{
            save: async (patch) => {
              replaceBot(await rpc.bots.update({ botId: active.id, ...patch }));
            },
            setAbility: async (ability, enabled) => {
              replaceBot(await rpc.bots.setAbility({ botId: active.id, ability, enabled }));
            },
            setComputer: async (mode) => {
              replaceBot(await rpc.bots.setComputer({ botId: active.id, mode }));
            },
            onOpenConnections: () => openSettings("models"),
            onExport: async () => {
              let token: string;
              try {
                ({ token } = await withIdentityConfirmation(() =>
                  rpc.export.teammateToken({ botId: active.id }),
                ));
              } catch (cause) {
                if (isIdentityConfirmationRequired(cause)) return;
                throw cause;
              }
              // A download link, so the browser or Electron saves the zip as it streams.
              const link = document.createElement("a");
              link.href = `/api/teammates/export?token=${encodeURIComponent(token)}`;
              link.download = "";
              link.click();
            },
            onClear: () => onClearBot(active),
          }}
        />
      ) : null}
      {identityDialog}
    </>
  );
}
